import {
  ETIQUETA_INSTRUMENTO,
  ETIQUETA_INSTRUMENTO_EN,
  FinanciacionDeExportacionSchema,
  type Financiador,
  type InstrumentoDeCredito,
  type PorReasignar,
} from '@cnc/contracts';
import {useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {Link} from 'react-router';
import {pedir} from '../api/cliente';
import {usePorReasignar} from '../api/financiacion';
import {et, tr} from '../i18n/idioma';
import {fecha, usd} from '../utilidades/formato';
import {AYUDA_TERMINOS_DE_FINANCIADOR as T} from './ayuda-financiacion-bluequest';
import {NotaDeAyuda} from './nota-de-ayuda';

const campo = 'rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta';

// Financiaciones con LC o BC genericos (financiador inactivo) por pasar a su
// banco (Daniel, 15/10/2026): en cada una se elige el banco y el tipo de credito.
export function PorReasignarABanco({financiadores}: {financiadores: Financiador[]}) {
  const consulta = usePorReasignar();
  const lista = consulta.data?.financiaciones ?? [];
  const bancos = financiadores.filter(f => f.activo && f.financia && f.instrumentos.length > 0);
  const [soloAbiertas, setSoloAbiertas] = useState(true);
  if (!lista.length) return null;
  const visibles = soloAbiertas ? lista.filter(x => x.abierta) : lista;
  return (
    <section
      className="space-y-2 rounded-lg border border-dorado bg-dorado-claro px-4 py-3"
      aria-label={tr('Por pasar a su banco', 'To move to their bank')}
      data-prueba="por-reasignar"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium">
          {tr(
            `Por pasar a su banco: ${lista.length} financiaciones`,
            `To move to their bank: ${lista.length} financings`,
          )}
          <NotaDeAyuda texto={T.porReasignar} />
        </h2>
        <label className="flex items-center gap-1 text-xs text-apagado">
          <input type="checkbox" checked={soloAbiertas} onChange={e => setSoloAbiertas(e.target.checked)} />
          {tr('Solo las abiertas', 'Only open ones')}
        </label>
      </div>
      {!bancos.length && (
        <p className="text-xs">
          {tr(
            'Primero crea los bancos con sus tipos de crédito ("＋ Nuevo financiador").',
            'First create the banks with their credit types ("＋ New financer").',
          )}
        </p>
      )}
      <ul className="space-y-1">
        {visibles.map(x => (
          <FilaPorReasignar key={x.exportacion} x={x} bancos={bancos} />
        ))}
      </ul>
    </section>
  );
}

function FilaPorReasignar({x, bancos}: {x: PorReasignar['financiaciones'][number]; bancos: Financiador[]}) {
  const cliente = useQueryClient();
  const etiqueta = et(ETIQUETA_INSTRUMENTO, ETIQUETA_INSTRUMENTO_EN);
  const [banco, setBanco] = useState('');
  const [tipo, setTipo] = useState<InstrumentoDeCredito | ''>(x.instrumentoSugerido ?? '');
  const [estado, setEstado] = useState<{texto: string; error: boolean} | null>(null);
  const [guardando, setGuardando] = useState(false);
  const elegido = bancos.find(b => b.codigo === banco);
  const tipos = elegido?.instrumentos ?? [];
  const guardar = async () => {
    setGuardando(true);
    setEstado(null);
    try {
      await pedir(
        `seguimiento/exportaciones/${encodeURIComponent(x.exportacion)}/financiacion`,
        FinanciacionDeExportacionSchema,
        {
          method: 'PUT',
          headers: {'content-type': 'application/json'},
          body: JSON.stringify({version: x.version, campos: {financiador: banco, instrumento: tipo || null}}),
        },
      );
      await cliente.invalidateQueries({queryKey: ['financiacion']});
    } catch (error) {
      setEstado({texto: error instanceof Error ? error.message : String(error), error: true});
    } finally {
      setGuardando(false);
    }
  };
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <Link
        to={`/seguimiento/${encodeURIComponent(x.exportacion)}#financiacion`}
        className="w-36 font-medium text-verde hover:underline"
      >
        {x.exportacion}
      </Link>
      <span className="w-12 text-xs text-apagado">{x.financiador}</span>
      <span className="w-28 text-right text-xs tabular-nums">
        {x.financiadoUsd !== null ? usd(x.financiadoUsd) : '—'}
      </span>
      <span className="w-24 text-xs text-apagado">{x.zarpe ? fecha(x.zarpe) : '—'}</span>
      <select
        value={banco}
        onChange={e => {
          setBanco(e.target.value);
          const nuevos = bancos.find(b => b.codigo === e.target.value)?.instrumentos ?? [];
          if (tipo && !nuevos.includes(tipo)) setTipo(nuevos.length === 1 ? nuevos[0]! : '');
          else if (!tipo && nuevos.length === 1) setTipo(nuevos[0]!);
        }}
        aria-label={tr(`Banco de ${x.exportacion}`, `${x.exportacion} bank`)}
        className={campo}
      >
        <option value="">{tr('— Banco —', '— Bank —')}</option>
        {bancos.map(b => (
          <option key={b.codigo} value={b.codigo}>
            {b.nombre === b.codigo ? b.codigo : `${b.codigo} · ${b.nombre}`}
          </option>
        ))}
      </select>
      <select
        value={tipo}
        onChange={e => setTipo(e.target.value as InstrumentoDeCredito | '')}
        aria-label={tr(`Tipo de crédito de ${x.exportacion}`, `${x.exportacion} credit type`)}
        className={campo}
        disabled={!banco}
      >
        <option value="">{tr('— Tipo —', '— Type —')}</option>
        {tipos.map(i => (
          <option key={i} value={i}>
            {etiqueta[i]}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!banco || !tipo || guardando}
        onClick={() => void guardar()}
        className="rounded-md bg-verde-oscuro px-3 py-1 text-sm text-white disabled:opacity-50"
      >
        {tr('Pasar', 'Move')}
      </button>
      {estado && <span className={`text-xs ${estado.error ? 'text-alerta' : ''}`}>{estado.texto}</span>}
    </li>
  );
}
